export interface ClienteDePeriodoFechado {
  closedPeriod: {
    get: (parametros: { id: string, month: number, year: number }) => Promise<unknown>
  }
}

export interface PeriodoAtualDaFolha {
  startDate: string
  endDate: string
}

export interface AlvoDeNavegacao {
  path: string
  query?: QueryDaRota
  state?: unknown
}

export type QueryDaRota = Record<string, string | string[] | null | undefined>

const ROTA_DE_FERIAS_DO_EMPREGADO = /^\/gerenciamento-de-ferias\/empregado\/[0-9a-f]{24}$/

export type AlvoDaFolhaComPeriodo = AlvoDeNavegacao | {
  name: 'timesheet'
  params: {
    type: 'employee'
    startDate: string
    endDate: string
    id: string
  }
  query?: QueryDaRota
  state?: unknown
}

function separarCaminhoEQuery (caminhoComQuery: string): { caminho: string, query: QueryDaRota } {
  const indiceDaQuery = caminhoComQuery.indexOf('?')
  if (indiceDaQuery === -1) return { caminho: caminhoComQuery, query: {} }

  const caminho = caminhoComQuery.slice(0, indiceDaQuery)
  const parametros = new URLSearchParams(caminhoComQuery.slice(indiceDaQuery + 1))
  const query: QueryDaRota = {}

  parametros.forEach((valor, chave) => {
    const valorAtual = query[chave]
    query[chave] = Array.isArray(valorAtual) ? [...valorAtual, valor] : typeof valorAtual === 'string' ? [valorAtual, valor] : valor
  })

  return { caminho, query }
}

type RespostaDePeriodoFechado = {
  startPeriod?: unknown
  endPeriod?: unknown
}

function extrairPeriodoFechado (resposta: unknown): RespostaDePeriodoFechado | null {
  if (!resposta || typeof resposta !== 'object') return null

  const respostaComoObjeto = resposta as { data?: { success?: unknown }, body?: { success?: unknown } }
  const periodo = respostaComoObjeto.data?.success ?? respostaComoObjeto.body?.success

  return periodo && typeof periodo === 'object' ? periodo as RespostaDePeriodoFechado : null
}

function formatarDataEstrita (valor: unknown): string {
  if (typeof valor !== 'string') throw new Error('Data do período de fechamento inválida')

  const correspondencia = /^(\d{4})-(\d{2})-(\d{2})(?:T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:0\d|1\d|2[0-3]):?[0-5]\d)?)?$/.exec(valor)
  if (!correspondencia) throw new Error('Data do período de fechamento inválida')

  const [, ano, mes, dia] = correspondencia
  const data = new Date(Date.UTC(Number(ano), Number(mes) - 1, Number(dia)))
  const ehMesmoDia = data.getUTCFullYear() === Number(ano) && data.getUTCMonth() === Number(mes) - 1 && data.getUTCDate() === Number(dia)

  if (!ehMesmoDia) throw new Error('Data do período de fechamento inválida')

  return `${ano}-${mes}-${dia}`
}

function obterMesEAnoDeReferencia (agora: () => Date, diaFocal?: string, mesDeReferencia?: unknown): { month: number, year: number } {
  if (mesDeReferencia !== undefined) {
    if (typeof mesDeReferencia !== 'string' || !/^[0-9]{4}-(?:0[1-9]|1[0-2])$/.test(mesDeReferencia) || mesDeReferencia.startsWith('0000-')) {
      throw new Error('Mês de referência inválido')
    }
    const [year, month] = mesDeReferencia.split('-').map(Number)
    if (diaFocal !== undefined) {
      const referenciaDoDia = obterMesEAnoDeReferencia(agora, diaFocal)
      if (referenciaDoDia.year !== year || referenciaDoDia.month !== month) throw new Error('Mês de referência divergente do dia focal')
    }
    return { year, month }
  }
  if (diaFocal !== undefined) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(diaFocal)) throw new Error('Dia focal inválido')

    const [ano, mes, dia] = diaFocal.split('-').map(Number)
    const data = new Date(Date.UTC(ano, mes - 1, dia))
    const ehMesmoDia = data.getUTCFullYear() === ano && data.getUTCMonth() === mes - 1 && data.getUTCDate() === dia
    if (!ehMesmoDia) throw new Error('Dia focal inválido')

    return { month: mes, year: ano }
  }

  const dataAtual = agora()
  if (!(dataAtual instanceof Date) || Number.isNaN(dataAtual.getTime())) {
    throw new Error('Relógio inválido')
  }

  return { month: dataAtual.getMonth() + 1, year: dataAtual.getFullYear() }
}

export async function obterPeriodoAtualDaFolha (
  cliente: ClienteDePeriodoFechado,
  id: string,
  agora: () => Date,
  diaFocal?: string,
  mesDeReferencia?: unknown
): Promise<PeriodoAtualDaFolha> {
  const { month, year } = obterMesEAnoDeReferencia(agora, diaFocal, mesDeReferencia)

  const resposta = await cliente.closedPeriod.get({
    id,
    month,
    year
  })
  const periodo = extrairPeriodoFechado(resposta)

  if (!periodo) throw new Error('Período de fechamento indisponível')

  const startDate = formatarDataEstrita(periodo.startPeriod)
  const endDate = formatarDataEstrita(periodo.endPeriod)
  if (startDate > endDate) throw new Error('Período de fechamento inválido')

  return { startDate, endDate }
}

export async function resolverAlvoDaFolhaAtual (
  cliente: ClienteDePeriodoFechado,
  alvo: AlvoDeNavegacao,
  agora: () => Date
): Promise<AlvoDaFolhaComPeriodo> {
  const { caminho, query } = separarCaminhoEQuery(alvo.path)
  if (ROTA_DE_FERIAS_DO_EMPREGADO.test(caminho) ||
      (caminho === '/approvals' && query.acao === 'consultar_solicitacoes')) {
    // O Router recebe a query separada; o formulário a valida e consome uma vez.
    return { ...alvo, path: caminho, query }
  }
  const correspondencia = /^\/timesheet\/employee\/([0-9a-f]{15,})(?:\/(\d{4}-\d{2}-\d{2}))?\/?$/.exec(caminho)
  if (!correspondencia) {
    if (!Object.keys(query).length) return alvo
    return { ...alvo, path: caminho, query: { ...alvo.query, ...query } }
  }

  const id = correspondencia[1]
  const dia = correspondencia[2]
  const { startDate, endDate } = await obterPeriodoAtualDaFolha(cliente, id, agora, dia, query.mes)

  return {
    name: 'timesheet',
    params: { type: 'employee', startDate, endDate, id },
    ...(dia || Object.keys(query).length ? { query: { ...query, ...(dia ? { dia } : {}) } } : {}),
    state: alvo.state
  }
}

export function rotasEquivalentesDaFolhaAtual (esperada: string, atual: string): boolean {
  if (esperada === atual) return true

  const { caminho: caminhoEsperado, query } = separarCaminhoEQuery(esperada)
  const { caminho: caminhoAtual } = separarCaminhoEQuery(atual)
  if (ROTA_DE_FERIAS_DO_EMPREGADO.test(caminhoEsperado) ||
      (caminhoEsperado === '/approvals' && query.acao === 'consultar_solicitacoes') ||
      (caminhoEsperado === '/acompanhamento-tempo-real' && query.acao === 'consultar_acompanhamento_tempo_real')) {
    // Confirma apenas o destino, inclusive depois de a tela consumir o rascunho.
    return caminhoEsperado === caminhoAtual
  }
  const rotaCurta = /^\/timesheet\/employee\/([0-9a-f]{15,})(?:\/(\d{4}-\d{2}-\d{2}))?\/?$/.exec(caminhoEsperado)
  if (!rotaCurta) return false

  if (rotaCurta[2] || query.mes !== undefined) {
    try {
      obterMesEAnoDeReferencia(() => new Date(), rotaCurta[2], query.mes)
    } catch {
      return false
    }
  }

  const rotaCanonica = /^\/timesheet\/employee\/\d{4}-\d{2}-\d{2}\/(?:\d{4}-\d{2}-\d{2}\/)?([0-9a-f]{15,})\/?$/.exec(caminhoAtual)
  return rotaCanonica?.[1] === rotaCurta[1]
}
